import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  NoPreloading,
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { NgxFileDropModule } from 'ngx-file-drop';
import { QuillModule } from 'ngx-quill';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';

import { appRoutes } from 'app/app.routing';
import { appConfig as fuseAppConfig } from 'app/core/config/app.config';
import { CoreModule } from 'app/core/core.module';
import { CustomTranslateLoader } from 'app/core/services/translations/custom-translate-loader.service';
import { mockApiServices } from 'app/mock-api';
import { PcAlertModule } from 'app/shared/components/alert';
import { PcConfigModule } from 'app/shared/services/config';
import { PcMockApiModule } from 'app/shared/lib/mock-api';
import { PcModule } from 'app/shared/pc-core.module';
import { PcSplashScreenModule } from 'app/shared/services/splash-screen';
import { KIT_PROVIDERS } from 'app/kit.providers';

/**
 * Standalone application configuration (replaces the former AppModule).
 *
 * Most feature areas are still NgModules; they are pulled in via
 * importProvidersFrom() so their DI wiring (HTTP interceptors, MSAL providers,
 * Pc/Fuse config + mock-api) keeps working unchanged. They will be converted to
 * standalone incrementally. zoneless change detection (no zone.js)
 * — the switch to zoneless happens in the final migration phase.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideRouter(
      appRoutes,
      // Preloading every lazy module is worth it in production (few bundled
      // chunks, fetched in the background) but floods the dev server with
      // hundreds of unbundled module requests on every reload. isDevMode() is
      // used instead of environment.production because the base environment.ts
      // sets production: true even for ng serve.
      withPreloading(isDevMode() ? NoPreloading : PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),

    provideAnimations(),

    importProvidersFrom(
      // i18n — keep the CustomTranslateLoader (per-instance remote translations
      // merged over local /assets/i18n fallback).
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: CustomTranslateLoader,
          deps: [HttpClient],
        },
      }),

      // Third-party UI modules used app-wide
      BsDatepickerModule,
      TimepickerModule,
      QuillModule.forRoot(),
      CKEditorModule,
      NgxFileDropModule,

      // Pc / Fuse roots (config, splash-screen, mock-api, alert) + auth/http via CoreModule
      PcModule,
      PcSplashScreenModule,
      PcConfigModule.forRoot(fuseAppConfig),
      PcMockApiModule.forRoot(mockApiServices),
      CoreModule,
      PcAlertModule
    ),

    TitleCasePipe,
    DatePipe,
    TranslateService,
    ...KIT_PROVIDERS,
  ],
};
